import { AnyAction } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import {
  AlignBottom,
  Book1,
  Danger,
  HambergerMenu,
  Logout,
  Map1,
  ScanBarcode,
  VolumeHigh,
  VolumeSlash,
} from 'iconsax-react'
import { Dialog } from 'primereact/dialog'
import { Sidebar } from 'primereact/sidebar'
import { useState, Dispatch, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as DOMPurify from 'dompurify'
import LoadingSpinner from '../../../components/LoadingSpinner'
import {
  fetchCollectedItemsRequest,
  fetchGameInstructionsRequest,
  fetchLegalUrlsRequest,
} from '../../../redux/actions/gameActions'
import QRCode from 'qrcode.react'
import Language from './Language'
import { useLocation, useNavigate } from 'react-router-dom'
import { InputSwitch } from 'primereact/inputswitch'
import useSound from 'use-sound'
import inputChangeSound from '../../../assets/audio/input-sound.wav'
import { Slider } from 'primereact/slider'
import { CHANGE_GLOBAL_VOLUME } from '../../../redux/actionTypes/gameActionTypes'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'
import clickSoundEffect from '../../../assets/audio/click2.wav'
import { useTranslation } from 'react-i18next'
import jwt_decode from 'jwt-decode'
import { GameState } from '../../../redux/types/game'
import { getGameSelector } from '../../../redux/selectors/gameSelectors'
import Loading from '../../../components/Loading'
import InventoryIllustration from './InventoryIllustration'
import {
  generateCssClassNo,
  generateCssClassYes,
} from '../../../utils/confirmDialogClasses'
import { decodeIframeString } from '../../../utils/decodeIframeString'

const GameHeader = ({
  gameLogo,
  collectedItems,
  gameLoading,
  gameCode,
  hasInventory,
  teamName,
  showMapControls,
  showMapControlsRef,
  setShowMapControls,
  volumeGlobal,
  gameType,
  showScore,
  setShowScore,
}: {
  gameLogo: string
  collectedItems: any
  gameLoading: any
  gameCode: any
  hasInventory?: boolean
  teamName: string | null
  showMapControls: any
  showMapControlsRef: any
  setShowMapControls: any
  volumeGlobal: any
  gameType: string
  showScore: boolean
  setShowScore: any
}) => {
  const {
    data: { legalUrls, selectedLanguage, branding, instructionsHtml },
    loading: {
      isLoadingGameLocales,
      isLoadingCollectedItems,
      isLoadingInstructionsHtml,
    },
  }: GameState = useSelector(getGameSelector)
  const [displayInventory, setDisplayInventory] = useState(false)
  const [displayInstructions, setDisplayInstructions] = useState(false)

  const [displayScanCode, setDisplayScanCode] = useState(false)
  const [visibleRight, setVisibleRight] = useState(false)
  const token = localStorage.getItem('tokenCityHunt') ?? ''
  const location = useLocation()

  const dispatch: Dispatch<AnyAction> = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [inputSound] = useSound(inputChangeSound, {
    volume: volumeGlobal,
  })

  const [clickSound] = useSound(clickSoundEffect, {
    volume: volumeGlobal,
  })

  const { GameId }: any = jwt_decode(token)

  useEffect(() => {
    if (!instructionsHtml) {
      dispatch(
        fetchGameInstructionsRequest({
          gameId: GameId,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          localeId: selectedLanguage?.id,
        }),
      )
    }
  }, [displayInstructions])

  const items = [
    {
      label: t('Inventory'),
      icon: (
        <Map1
          color={branding?.menu.iconsColor}
          variant="Bulk"
          className="mr-2"
        />
      ),
      command: () => {
        if (!isLoadingGameLocales) {
          setDisplayInventory(true)
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          dispatch(fetchCollectedItemsRequest(selectedLanguage.id))
          inputSound()
        }
      },
    },
    {
      label: t('Leaderboard'),
      icon: (
        <AlignBottom
          color={branding?.menu.iconsColor}
          variant="Bulk"
          className="mr-2"
        />
      ),
      command: () => {
        inputSound()
        navigate('/leaderboard?showRateAndSocial=false')
      },
    },
    {
      label: t('AddTeamMate'),
      icon: (
        <ScanBarcode
          color={branding?.menu.iconsColor}
          variant="Bulk"
          className="mr-2"
        />
      ),
      command: () => {
        setDisplayScanCode(true)
        inputSound()
      },
    },
    {
      label: t('HowToPlay'),
      icon: (
        <Book1
          color={branding?.menu.iconsColor}
          variant="Bulk"
          className="mr-2"
        />
      ),
      command: () => {
        setDisplayInstructions(true)
        inputSound()
      },
    },
    {
      label: t('Logout'),
      icon: (
        <Logout
          color={branding?.menu.iconsColor}
          variant="Bulk"
          className="mr-2"
        />
      ),
      command: () => {
        confirmLogoutHandler()
      },
    },
  ]

  const accept = () => {
    clickSound()
    navigate('/')
  }

  const confirmLogoutHandler = () => {
    clickSound()
    confirmDialog({
      message: t('LogoutConfirm'),
      header: t('Logout'),
      icon: <Danger size="3rem" color="#333" variant="Bold" />,
      accept,
      onHide: () => inputSound(),
      acceptLabel: t('Yes'),
      rejectLabel: t('No'),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      acceptClassName: generateCssClassYes(branding),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      rejectClassName: generateCssClassNo(branding),
      style: {
        width: '90%',
      },
      headerStyle: {
        background: branding?.modals.backgroundColor,
      },
    })
  }

  const dialogFuncMap = {
    displayInventory: setDisplayInventory,
    displayScanCode: setDisplayScanCode,
    displayInstructions: setDisplayInstructions,
  }

  const onHide = (name: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    dialogFuncMap[`${name}`](false)
    inputSound()
  }

  const sanitizedData = (data: string) => ({
    __html: DOMPurify.sanitize(data, {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['src', 'class', 'allowfullscreen', 'frameborder'],
    }),
  })

  Sentry.setContext('Variables', {
    displayInventory,
    displayInstructions,
    displayScanCode,
    visibleRight,
    GameId,
  })

  return (
    <>
      <ConfirmDialog />
      <div
        style={{
          color: '#333',
          width: '100%',
          backgroundColor: branding?.header.backgroundColor,
        }}
        className="flex align-items-center justify-content-between p-2"
      >
        <div className="flex align-items-center">
          {gameLogo && (
            <img
              className="zoominleft animation-duration-1000"
              src={gameLogo}
              alt="Game"
              width={`${!gameLogo.includes('urbanavantura') ? '50px' : ''}`}
            />
          )}
        </div>
        <div className="flex align-items-center">
          {hasInventory && (
            <Map1
              color={branding?.header.iconColor}
              className="mr-2 zoominright animation-duration-1000"
              size="2.5rem"
              onClick={() => {
                if (!isLoadingGameLocales) {
                  setDisplayInventory(true)
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  dispatch(fetchCollectedItemsRequest(selectedLanguage?.id))
                  inputSound()
                }
              }}
              variant="Bulk"
            />
          )}
          <HambergerMenu
            onClick={() => {
              setVisibleRight(true)
              inputSound()
              if (!legalUrls) {
                dispatch(fetchLegalUrlsRequest())
              }
            }}
            size="3rem"
            color={branding?.header.iconColor}
            variant="Bulk"
            className="zoominright animation-duration-1000"
          />
        </div>
        <Sidebar
          visible={visibleRight}
          position="right"
          onHide={() => {
            setVisibleRight(false)
            inputSound()
          }}
          style={{ backgroundColor: branding?.menu.backgroundColor }}
          icons={() => {
            return (
              <h2 className="m-0" style={{ color: branding?.menu.textColor }}>
                {t('Menu')}
              </h2>
            )
          }}
        >
          <div className="flex align-items-center my-2">
            <h1
              className="mb-5"
              style={{
                color: branding?.menu.iconsColor,
                overflowWrap: 'inherit',
              }}
            >
              {teamName}
            </h1>
          </div>
          {!hasInventory
            ? items
                .filter((el) => el.label !== t('Inventory'))
                .map((item) => {
                  return (
                    <>
                      <div
                        key={item.label}
                        className="flex align-items-center my-2"
                        onClick={() => item.command()}
                      >
                        {item.icon}
                        <span style={{ color: branding?.menu.textColor }}>
                          {item.label}
                        </span>
                      </div>
                      <hr />
                    </>
                  )
                })
            : items.map((item) => {
                return (
                  <>
                    <div
                      key={item.label}
                      className="flex align-items-center my-2"
                      onClick={() => item.command()}
                    >
                      {item.icon}
                      <span style={{ color: branding?.menu.textColor }}>
                        {item.label}
                      </span>
                    </div>
                    <hr />
                  </>
                )
              })}
          {gameType !== 'Indoor' && location.pathname !== '/welcome' && (
            <div className="my-3">
              <h5 style={{ color: branding?.menu.textColor }}>
                {t('MapControls')}
              </h5>
              <InputSwitch
                checked={showMapControls}
                onChange={(event: any) => {
                  setShowMapControls((prevState: boolean) => !prevState)
                  showMapControlsRef.current = event.value
                  inputSound()
                }}
                style={{
                  backgroundColor: branding?.menu.iconsColor,
                  borderRadius: '30px',
                }}
              />
            </div>
          )}
          {location.pathname !== '/welcome' && (
            <div className="my-3">
              <h5 style={{ color: branding?.menu.textColor }}>
                {t('ScoreOnMap')}
              </h5>
              <InputSwitch
                checked={showScore}
                onChange={() => {
                  setShowScore((prevState: boolean) => !prevState)
                  inputSound()
                }}
                style={{
                  backgroundColor: branding?.menu.iconsColor,
                  borderRadius: '30px',
                }}
              />
            </div>
          )}
          <div className="my-3">
            <h5 style={{ color: branding?.menu.textColor }}>{t('Language')}</h5>
            <Language style={{ width: '100%' }} />
          </div>
          <div className="my-3">
            <h5 style={{ color: branding?.menu.textColor }}>{t('Volume')}</h5>
            <div className="flex align-items-center">
              {volumeGlobal > 0 ? (
                <VolumeHigh
                  size="3rem"
                  color={branding?.menu.iconsColor}
                  variant="Bulk"
                  onClick={() =>
                    dispatch({ type: CHANGE_GLOBAL_VOLUME, payload: 0 })
                  }
                />
              ) : (
                <VolumeSlash
                  size="3rem"
                  color={branding?.menu.iconsColor}
                  variant="Bulk"
                  onClick={() =>
                    dispatch({ type: CHANGE_GLOBAL_VOLUME, payload: 90 })
                  }
                />
              )}
              <Slider
                value={volumeGlobal * 100}
                onChange={(e) =>
                  dispatch({ type: CHANGE_GLOBAL_VOLUME, payload: e.value })
                }
                orientation="horizontal"
                className="w-full ml-3"
              />
            </div>
            {legalUrls?.faqUrl && (
              <div className="my-3">
                <a href={legalUrls.faqUrl} target="_blank" rel="noreferrer">
                  <h4
                    style={{
                      color: branding?.menu.textColor,
                      borderBottom: '0.5px solid #f15a22',
                      paddingBottom: '1rem',
                    }}
                  >
                    {t('FAQ')}
                  </h4>
                </a>
              </div>
            )}
            {legalUrls?.privacyPolicyUrl && (
              <div className="my-3">
                <a
                  href={legalUrls.privacyPolicyUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <h4
                    style={{
                      color: branding?.menu.textColor,
                      borderBottom: '0.5px solid #f15a22',
                      paddingBottom: '1rem',
                    }}
                  >
                    {t('PrivacyPolicy')}
                  </h4>
                </a>
              </div>
            )}
            {legalUrls?.termsUrl && (
              <div className="my-3">
                <a href={legalUrls.termsUrl} target="_blank" rel="noreferrer">
                  <h4
                    style={{
                      color: branding?.menu.textColor,
                      borderBottom: '0.5px solid #f15a22',
                      paddingBottom: '1rem',
                    }}
                  >
                    {t('TermsAndConditions')}
                  </h4>
                </a>
              </div>
            )}
          </div>
        </Sidebar>
      </div>
      <Dialog
        header={t('Inventory')}
        visible={displayInventory}
        style={{ width: '94vw', height: '100%' }}
        onHide={() => onHide('displayInventory')}
        headerStyle={{
          backgroundColor: branding?.modals.backgroundColor,
          color: branding?.modals.textColor,
        }}
      >
        <div className="mt-2 flex flex-column align-items-center">
          {!isLoadingCollectedItems ? (
            collectedItems.length ? (
              collectedItems?.map((item: any, index: number) => {
                return (
                  <img
                    key={String(index + 1)}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    src={item?.fileUrl}
                    // src="/assets/layout/images/logo.png"
                    alt="inventory"
                    // className="md:ml-auto block md:h-full mb-4"
                    className="mb-4"
                    style={{
                      // clipPath: 'polygon(8% 0, 100% 0%, 100% 100%, 0 100%)',
                      width: 'inherit',
                      maxWidth: '100%',
                    }}
                  />
                )
              })
            ) : (
              <InventoryIllustration
                clothesColor={branding?.illustrations.clothesColor}
                skinColor={branding?.illustrations.skinColor}
                redPuzzleColor={branding?.illustrations.redPuzzleColor}
              />
            )
          ) : (
            <LoadingSpinner />
          )}
        </div>
      </Dialog>
      <Dialog
        header={t('ScanQRCodeTitle')}
        visible={displayScanCode}
        style={{ width: '94vw', height: '100%' }}
        onHide={() => onHide('displayScanCode')}
        headerStyle={{
          backgroundColor: branding?.modals.backgroundColor,
          color: branding?.modals.textColor,
        }}
      >
        {gameLoading.isCheckingGameCode ? (
          <LoadingSpinner />
        ) : (
          gameCode && (
            <div className="flex justify-content-center">
              <QRCode
                id="123456"
                value={`${window.location.hostname}/qr-code-login-validate/${gameCode}`}
                size={290}
                level={'H'}
                includeMargin={true}
                width="100%"
              />
            </div>
          )
        )}
      </Dialog>
      <Dialog
        header={t('HowToPlay')}
        visible={displayInstructions}
        style={{ width: '94vw', height: '100%' }}
        onHide={() => onHide('displayInstructions')}
        headerStyle={{
          backgroundColor: branding?.modals.backgroundColor,
          color: branding?.modals.textColor,
        }}
      >
        {!isLoadingInstructionsHtml ? (
          <div
            style={{ overflowWrap: 'break-word', overflowY: 'visible' }}
            className="img-full-width ql-editor my-3"
            dangerouslySetInnerHTML={sanitizedData(
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              decodeIframeString(instructionsHtml?.html ?? '') ?? '',
            )}
          />
        ) : (
          <Loading />
        )}
      </Dialog>
    </>
  )
}

export default GameHeader
